import React, { useCallback, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { radii, spacing, typographyScale } from "../../../styles/design-system";

interface McqAssessmentQuestionCardProps {
  question: string;
  choices: string[];
  showHasMultipleAnswersHint?: boolean;
  selectedChoiceIndices: number[];
  onSelectChoice: (index: number) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
}

const McqAssessmentQuestionCard = ({
  question,
  choices,
  showHasMultipleAnswersHint = false,
  selectedChoiceIndices,
  onSelectChoice,
  onSubmit,
  isSubmitting = false,
  disabled = false,
}: McqAssessmentQuestionCardProps) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [focusRingVisible, setFocusRingVisible] = useState<Record<number, boolean>>({});
  const optionRefs = useRef<Array<{ focus?: () => void } | null>>([]);

  const isDisabled = disabled || isSubmitting;

  const focusOption = useCallback((index: number) => {
    setFocusedIndex(index);
    const ref = optionRefs.current[index];
    if (ref && typeof ref.focus === "function") {
      ref.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: { nativeEvent?: { key?: string }; key?: string; preventDefault?: () => void }, index: number) => {
      const key = (e as { key?: string }).key ?? (e.nativeEvent as { key?: string } | undefined)?.key;
      if (!key) return;

      const last = choices.length - 1;

      switch (key) {
        case "ArrowDown": {
          e.preventDefault?.();
          const next = index < last ? index + 1 : 0;
          focusOption(next);
          break;
        }
        case "ArrowUp": {
          e.preventDefault?.();
          const prev = index > 0 ? index - 1 : last;
          focusOption(prev);
          break;
        }
        case "Home": {
          e.preventDefault?.();
          focusOption(0);
          break;
        }
        case "End": {
          e.preventDefault?.();
          focusOption(last);
          break;
        }
        case " ":
        case "Space": {
          e.preventDefault?.();
          if (!isDisabled) {
            onSelectChoice(index);
          }
          break;
        }
        default:
          break;
      }
    },
    [choices.length, focusOption, isDisabled, onSelectChoice]
  );

  return (
    <View style={styles.container}>
      <View style={styles.questionHeader}>
        <CustomText
          text={question}
          useTranslationText={false}
          size="xl"
          weight="ultraBold"
          style={styles.questionText}
        />
        {showHasMultipleAnswersHint && (
          <View style={styles.selectAllHintBadge}>
            <CustomText
              text="Select all that apply"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.selectAllHintText}
            />
          </View>
        )}
      </View>

      <View
        style={styles.optionsContainer}
        role={showHasMultipleAnswersHint ? "group" : "radiogroup"}
        accessibilityRole={showHasMultipleAnswersHint ? undefined : "radiogroup"}
        aria-label={showHasMultipleAnswersHint ? "Answer choices — select all that apply" : "Answer choices"}
        accessibilityLabel={showHasMultipleAnswersHint ? "Answer choices — select all that apply" : "Answer choices"}>
        {choices.map((choice, index) => {
          const isSelected = selectedChoiceIndices.includes(index);
          const hasFocusRing = focusRingVisible[index] === true;
          const tabIndex = focusedIndex === index ? 0 : -1;

          return (
            <Pressable
              key={`${choice}-${index}`}
              ref={(el) => {
                optionRefs.current[index] = el as { focus?: () => void } | null;
              }}
              onPress={() => {
                if (!isDisabled) {
                  setFocusedIndex(index);
                  onSelectChoice(index);
                }
              }}
              // @ts-expect-error onKeyDown is a web-only prop not declared in RN's Pressable types
              onKeyDown={(e: { key?: string; preventDefault?: () => void }) => handleKeyDown(e, index)}
              style={[
                styles.optionRow,
                isSelected && styles.optionRowSelected,
                hasFocusRing && styles.optionRowFocused,
              ]}
              role={showHasMultipleAnswersHint ? "checkbox" : "radio"}
              aria-checked={isSelected}
              aria-disabled={isDisabled}
              tabIndex={tabIndex}
              accessibilityRole={showHasMultipleAnswersHint ? "checkbox" : "radio"}
              accessibilityState={{ checked: isSelected, disabled: isDisabled }}
              accessibilityLabel={`Option ${index + 1}: ${choice}`}
              onFocus={() => {
                setFocusedIndex(index);
                setFocusRingVisible((prev) => ({ ...prev, [index]: true }));
              }}
              onBlur={() => {
                setFocusRingVisible((prev) => ({ ...prev, [index]: false }));
              }}
              disabled={isDisabled}>
              <View style={[styles.optionIndexBadge, isSelected && styles.optionIndexBadgeSelected]}>
                <CustomText
                  text={(index + 1).toString()}
                  useTranslationText={false}
                  size="s"
                  weight="ultraBold"
                  style={[styles.optionIndexText, isSelected && styles.optionIndexTextSelected]}
                />
              </View>
              <CustomText
                text={choice}
                useTranslationText={false}
                size="m"
                style={[styles.optionText, isSelected && styles.optionTextSelected]}
              />
            </Pressable>
          );
        })}
      </View>

      <CustomButton
        text={isSubmitting ? "Submitting..." : "Submit Answer"}
        onPress={onSubmit}
        styleType="primary"
        style={styles.submitButton}
        textStyle={styles.submitButtonText}
        disabled={disabled || isSubmitting || selectedChoiceIndices.length === 0}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 700,
    alignItems: "center",
    gap: 16,
  },
  questionHeader: {
    alignItems: "center",
    gap: spacing.xxs,
  },
  questionText: {
    color: "#101828",
    textAlign: "center",
  },
  selectAllHintBadge: {
    alignSelf: "center",
    backgroundColor: "rgb(245, 240, 254)",
    paddingHorizontal: spacing.xs,
    borderRadius: radii.full,
    borderColor: "rgb(212, 194, 247)",
    marginTop: spacing.xs,
    borderStyle: "solid",
    borderWidth: 1,
  },
  selectAllHintText: {
    color: "rgb(114, 68, 187)",
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    paddingVertical: spacing.xxs,
  },
  optionsContainer: {
    width: "100%",
    gap: 10,
  },
  optionRow: {
    width: "100%",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFFFFF",
    minHeight: 48,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  optionRowSelected: {
    borderColor: "#3E68FE",
    backgroundColor: "#EEF4FF",
  },
  optionRowFocused: {
    borderColor: "#3E68FE",
    // @ts-expect-error outline* are web-only CSS properties not in RN StyleSheet types
    outlineStyle: "solid",
    outlineWidth: 2,
    outlineColor: "#3E68FE",
    outlineOffset: 2,
  },
  optionIndexBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
  },
  optionIndexBadgeSelected: {
    borderColor: "#3E68FE",
    backgroundColor: "#DDE6FF",
  },
  optionIndexText: {
    color: "#667085",
  },
  optionIndexTextSelected: {
    color: "#2643B8",
  },
  optionText: {
    color: "#344054",
    flexShrink: 1,
  },
  optionTextSelected: {
    color: "#1D2939",
  },
  submitButton: {
    width: "100%",
    maxWidth: 700,
    height: 44,
    borderRadius: 8,
    marginTop: 4,
  },
  submitButtonText: {
    fontFamily: "Inter",
    fontWeight: "700",
    fontSize: 14,
  },
});

export default McqAssessmentQuestionCard;
